import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  ChevronLeft,
  Play,
  CheckCircle2,
  XCircle,
  Loader2,
  Brain,
  Target,
  Lightbulb,
  ChevronRight,
  BookOpen,
  GraduationCap,
  Award,
  RefreshCw,
  Plus,
  Clock,
  Trash2,
  History,
  MoreVertical,
  Check,
  X,
  Sparkles,
  ArrowRight,
  FileText
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { formatDistanceToNow } from 'date-fns';

export default function ReviewPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id: sessionId } = useParams();

  // View state: 'list' | 'setup' | 'session'
  const [view, setView] = useState(sessionId ? 'session' : 'list');
  
  // Sessions list state
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(true);

  // Setup state
  const [subjects, setSubjects] = useState([]);
  const [lectures, setLectures] = useState([]);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [selectedLectures, setSelectedLectures] = useState([]);
  const [totalQuestions, setTotalQuestions] = useState(10);
  const [loadingSetup, setLoadingSetup] = useState(false);

  // Session state
  const [session, setSession] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswer, setUserAnswer] = useState('');
  const [selectedOption, setSelectedOption] = useState('');
  const [evaluation, setEvaluation] = useState(null);
  const [loadingQuestion, setLoadingQuestion] = useState(false);
  const [submittingAnswer, setSubmittingAnswer] = useState(false);

  // Question generation options
  const [nextQuestionType, setNextQuestionType] = useState('auto');
  const [nextDifficulty, setNextDifficulty] = useState('auto');

  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [sessionToComplete, setSessionToComplete] = useState(null);

  useEffect(() => {
    if (sessionId) {
      setView('session');
      fetchSession();
    } else {
      setView('list');
      loadSessions();
    }
  }, [sessionId]);

  const loadSessions = async () => {
    setLoadingSessions(true);
    try {
      const data = await api.getReviewSessions();
      setSessions(data);
    } catch (error) {
      console.error('Failed to load sessions:', error);
      toast.error('Failed to load review sessions');
    } finally {
      setLoadingSessions(false);
    }
  };

  const fetchSession = async () => {
    try {
      const data = await api.getReviewSession(sessionId);
      setSession(data);
      
      // Set current question if available
      if (data.questions && data.questions.length > 0) {
        const lastAnsweredIndex = data.answers ? data.answers.length : 0;
        if (lastAnsweredIndex < data.questions.length) {
          const questionToShow = data.questions[lastAnsweredIndex];
          setCurrentQuestionIndex(questionToShow.index ?? lastAnsweredIndex);
          setCurrentQuestion(questionToShow);
        } else if (data.questions_generated < data.total_questions) {
          setCurrentQuestionIndex(data.questions.length);
        }
      }
    } catch (error) {
      console.error('Failed to load session:', error);
      toast.error('Failed to load review session');
      navigate('/review');
    }
  };

  const loadSetupData = async () => {
    setLoadingSetup(true);
    try {
      const [subjectsData, lecturesData] = await Promise.all([
        api.getSubjects(),
        api.getLectures()
      ]);
      setSubjects(subjectsData);
      setLectures(lecturesData.filter(l => l.raw_transcript || l.formatted_notes));
    } catch (error) {
      console.error('Failed to load setup data:', error);
      toast.error('Failed to load subjects and lectures');
    } finally {
      setLoadingSetup(false);
    }
  };

  const startNewReview = () => {
    setSelectedSubjects([]);
    setSelectedLectures([]);
    setTotalQuestions(10);
    setView('setup');
    loadSetupData();
  };

  const startReview = async () => {
    if (selectedSubjects.length === 0 && selectedLectures.length === 0) {
      toast.error('Please select at least one subject or lecture');
      return;
    }

    setLoadingQuestion(true);
    try {
      const newSession = await api.createReviewSession({
        title: `Review - ${new Date().toLocaleDateString()}`,
        lecture_ids: selectedLectures,
        subject_ids: selectedSubjects,
        total_questions: totalQuestions
      });
      
      setSession(newSession);
      setView('session');
      navigate(`/review/${newSession.id}`, { replace: true });
      
      // Generate first question
      await generateNextQuestion(newSession.id);
    } catch (error) {
      toast.error(error.message || 'Failed to start review');
    } finally {
      setLoadingQuestion(false);
    }
  };

  const generateNextQuestion = async (sessId = sessionId) => {
    setLoadingQuestion(true);
    setEvaluation(null);
    setUserAnswer('');
    setSelectedOption('');

    try {
      const result = await api.generateQuestion(sessId, {
        question_type: nextQuestionType === 'auto' ? undefined : nextQuestionType,
        difficulty: nextDifficulty === 'auto' ? undefined : nextDifficulty
      });
      
      setCurrentQuestion(result.question);
      setCurrentQuestionIndex(result.question.index);
      setSession(prev => ({
        ...prev,
        questions_generated: result.questions_generated,
        questions: [...(prev?.questions || []), result.question]
      }));
      
      setNextQuestionType('auto');
      setNextDifficulty('auto');
    } catch (error) {
      toast.error(error.message || 'Failed to generate question');
    } finally {
      setLoadingQuestion(false);
    }
  };

  const submitAnswer = async () => {
    const answer = currentQuestion?.type === 'multiple_choice' ? selectedOption : userAnswer;
    
    if (!answer.trim()) {
      toast.error('Please provide an answer');
      return;
    }

    const questionIndex = currentQuestion?.index ?? currentQuestionIndex;

    setSubmittingAnswer(true);
    try {
      const result = await api.submitAnswer(sessionId, questionIndex, answer);
      setEvaluation(result.evaluation);
      setSession(prev => ({
        ...prev,
        correct_count: result.correct_count,
        incorrect_count: result.incorrect_count,
        status: result.session_status,
        answers: [...(prev?.answers || []), result.evaluation]
      }));
    } catch (error) {
      toast.error(error.message || 'Failed to submit answer');
    } finally {
      setSubmittingAnswer(false);
    }
  };

  const handleMarkComplete = async (sess) => {
    try {
      await api.updateReviewSession(sess.id, { status: 'completed' });
      toast.success('Session marked as complete');
      if (sess.id === sessionId) {
        setSession(prev => ({ ...prev, status: 'completed' }));
      }
      loadSessions();
    } catch (error) {
      toast.error(error.message || 'Failed to mark session as complete');
    }
    setCompleteDialogOpen(false);
    setSessionToComplete(null);
  };

  const handleDeleteSession = async (sess) => {
    try {
      await api.deleteReviewSession(sess.id);
      toast.success('Session deleted');
      if (sess.id === sessionId) {
        navigate('/review');
      }
      loadSessions();
    } catch (error) {
      toast.error(error.message || 'Failed to delete session');
    }
    setDeleteDialogOpen(false);
    setSessionToDelete(null);
  };

  const createSessionReviewNote = async () => {
    if (!session) return;
    const noteMessages = [];
    if (currentQuestion?.question) {
      noteMessages.push({ role: 'assistant', content: currentQuestion.question });
    }
    if (currentQuestion?.type === 'multiple_choice') {
      const selectedLabel = currentQuestion.options?.find((option) => option.startsWith(selectedOption));
      if (selectedLabel) {
        noteMessages.push({ role: 'user', content: selectedLabel });
      }
    } else if (userAnswer.trim()) {
      noteMessages.push({ role: 'user', content: userAnswer.trim() });
    }
    if (evaluation?.feedback) {
      noteMessages.push({ role: 'assistant', content: evaluation.feedback });
    }
    try {
      const result = await api.createReviewNote({
        title: 'Quick Review note',
        source_surface: 'review',
        subject_id: session.subject_ids?.[0] || currentQuestion?.subject_id || null,
        lecture_ids: session.lecture_ids || [],
        review_session_id: session.id,
        messages: noteMessages,
      });
      toast.success(`Saved "${result.note?.title || 'review note'}" to Notes`);
    } catch (error) {
      toast.error(error.message || 'Failed to create review note');
    }
  };

  const toggleSubject = (subjectId) => {
    setSelectedSubjects(prev => 
      prev.includes(subjectId) 
        ? prev.filter(id => id !== subjectId)
        : [...prev, subjectId]
    );
  };

  const toggleLecture = (lectureId) => {
    setSelectedLectures(prev => 
      prev.includes(lectureId)
        ? prev.filter(id => id !== lectureId)
        : [...prev, lectureId]
    );
  };

  const getProgressPercentage = () => {
    if (!session) return 0;
    const answered = session.answers?.length || 0;
    return (answered / session.total_questions) * 100;
  };

  const isSessionComplete = session?.status === 'completed' || 
    (session?.answers?.length >= session?.total_questions);

  // ==================== Sessions List View ====================
  if (view === 'list') {
    const inProgressSessions = sessions.filter(s => s.status === 'in_progress');
    const completedSessions = sessions.filter(s => s.status === 'completed' || s.status === 'abandoned');

    return (
      <div className="min-h-screen bg-background noise-bg">
        <header className="glass sticky top-0 z-50">
          <div className="mx-auto flex h-auto min-h-16 max-w-4xl flex-wrap items-center justify-between gap-3 px-4 py-3 sm:h-16 sm:min-h-0 sm:flex-nowrap sm:px-6 sm:py-0">
            <div className="flex items-center">
              <Link to="/dashboard">
                <Button variant="ghost" size="icon">
                  <ChevronLeft className="w-5 h-5" />
                </Button>
              </Link>
              <span className="font-serif text-xl text-foreground ml-4">Review Sessions</span>
            </div>
            <Button onClick={startNewReview} className="rounded-xl" data-testid="new-review-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Review
            </Button>
          </div>
        </header>

        <main className="mx-auto max-w-4xl px-4 py-6 sm:px-6 sm:py-8">
          {loadingSessions ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : sessions.length === 0 ? (
            <div className="text-center py-20">
              <Brain className="w-16 h-16 text-muted-foreground/50 mx-auto mb-4" />
              <h2 className="font-serif text-2xl text-foreground mb-2">No Review Sessions Yet</h2>
              <p className="text-muted-foreground mb-6">
                Start your first review to test your knowledge with AI-generated questions
              </p>
              <Button onClick={startNewReview} className="rounded-xl" data-testid="first-review-btn">
                <Play className="w-4 h-4 mr-2" />
                Start Your First Review
              </Button>
            </div>
          ) : (
            <div className="space-y-8">
              {/* In Progress Sessions */}
              {inProgressSessions.length > 0 && (
                <div>
                  <h2 className="font-serif text-lg text-foreground mb-4 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-amber-500" />
                    In Progress
                  </h2>
                  <div className="space-y-3">
                    {inProgressSessions.map((sess) => (
                      <Card 
                        key={sess.id} 
                        className="border-border/50 shadow-card hover:border-primary/50 transition-colors"
                        data-testid={`session-${sess.id}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-3 mb-2">
                                <h3 className="font-medium text-foreground truncate">{sess.title}</h3>
                                <span className="text-xs px-2 py-1 rounded-full bg-amber-500/10 text-amber-600">
                                  In Progress
                                </span>
                              </div>
                              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                <span>{sess.answers?.length || 0} / {sess.total_questions} answered</span>
                                <span className="flex items-center gap-1">
                                  <CheckCircle2 className="w-3 h-3 text-green-500" />
                                  {sess.correct_count || 0}
                                </span>
                                <span className="flex items-center gap-1">
                                  <XCircle className="w-3 h-3 text-red-500" />
                                  {sess.incorrect_count || 0}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {formatDistanceToNow(new Date(sess.created_at), { addSuffix: true })}
                                </span>
                              </div>
                              <Progress 
                                value={((sess.answers?.length || 0) / sess.total_questions) * 100} 
                                className="h-1.5 mt-2" 
                              />
                            </div>
                            <div className="flex items-center gap-2 ml-4">
                              <Button 
                                onClick={() => navigate(`/review/${sess.id}`)}
                                className="rounded-xl"
                                data-testid={`resume-session-${sess.id}`}
                              >
                                <Play className="w-4 h-4 mr-2" />
                                Resume
                              </Button>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon">
                                    <MoreVertical className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => {
                                    setSessionToComplete(sess);
                                    setCompleteDialogOpen(true);
                                  }}>
                                    <Check className="w-4 h-4 mr-2" />
                                    Mark as Complete
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    className="text-destructive"
                                    onClick={() => {
                                      setSessionToDelete(sess);
                                      setDeleteDialogOpen(true);
                                    }}
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Completed Sessions */}
              {completedSessions.length > 0 && (
                <div>
                  <h2 className="font-serif text-lg text-foreground mb-4 flex items-center gap-2">
                    <History className="w-5 h-5 text-muted-foreground" />
                    History
                  </h2>
                  <div className="space-y-3">
                    {completedSessions.map((sess) => {
                      const score = sess.total_questions > 0 
                        ? Math.round(((sess.correct_count || 0) / sess.total_questions) * 100)
                        : 0;
                      return (
                        <Card 
                          key={sess.id} 
                          className="border-border/50 shadow-card"
                          data-testid={`session-${sess.id}`}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3 mb-2">
                                  <h3 className="font-medium text-foreground truncate">{sess.title}</h3>
                                  <span className={`text-xs px-2 py-1 rounded-full ${
                                    sess.status === 'abandoned' 
                                      ? 'bg-muted text-muted-foreground'
                                      : score >= 70 
                                        ? 'bg-green-500/10 text-green-600' 
                                        : 'bg-red-500/10 text-red-600'
                                  }`}>
                                    {sess.status === 'abandoned' ? 'Abandoned' : `${score}%`}
                                  </span>
                                </div>
                                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                  <span>{sess.answers?.length || 0} / {sess.total_questions} questions</span>
                                  <span className="flex items-center gap-1">
                                    <CheckCircle2 className="w-3 h-3 text-green-500" />
                                    {sess.correct_count || 0}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <XCircle className="w-3 h-3 text-red-500" />
                                    {sess.incorrect_count || 0}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {formatDistanceToNow(new Date(sess.created_at), { addSuffix: true })}
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 ml-4">
                                <Button 
                                  variant="outline"
                                  onClick={() => navigate(`/review/${sess.id}`)}
                                  className="rounded-xl"
                                  data-testid={`view-session-${sess.id}`}
                                >
                                  View Results
                                </Button>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon">
                                      <MoreVertical className="w-4 h-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem 
                                      className="text-destructive"
                                      onClick={() => {
                                        setSessionToDelete(sess);
                                        setDeleteDialogOpen(true);
                                      }}
                                    >
                                      <Trash2 className="w-4 h-4 mr-2" />
                                      Delete
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </main>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Review Session?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete this review session and all its questions and answers. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={() => sessionToDelete && handleDeleteSession(sessionToDelete)}
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Mark Complete Confirmation Dialog */}
        <AlertDialog open={completeDialogOpen} onOpenChange={setCompleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Mark Session as Complete?</AlertDialogTitle>
              <AlertDialogDescription>
                This will end the review session early. You can still view your results, but you won't be able to answer more questions.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => sessionToComplete && handleMarkComplete(sessionToComplete)}>
                Mark Complete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  }

  // ==================== Setup Screen ====================
  if (view === 'setup') {
    return (
      <div className="min-h-screen bg-background noise-bg">
        <header className="glass sticky top-0 z-50">
          <div className="mx-auto flex h-16 max-w-4xl items-center px-4 sm:px-6">
            <Button variant="ghost" size="icon" onClick={() => setView('list')}>
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <span className="font-serif text-xl text-foreground ml-4">New Review</span>
          </div>
        </header>

        <main className="mx-auto max-w-4xl px-4 py-6 sm:px-6 sm:py-8">
          <div className="text-center mb-8">
            <Brain className="w-16 h-16 text-primary mx-auto mb-4" />
            <h1 className="font-serif text-3xl text-foreground mb-2">Exam Preparation</h1>
            <p className="text-muted-foreground">
              Generate AI-curated questions from your lectures to test your knowledge
            </p>
          </div>

          {loadingSetup ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="space-y-8">
              {/* Subject Selection */}
              {subjects.length > 0 && (
                <Card className="border-border/50 shadow-card">
                  <CardHeader>
                    <CardTitle className="font-serif text-lg flex items-center gap-2">
                      <GraduationCap className="w-5 h-5" />
                      Select Subjects
                    </CardTitle>
                    <CardDescription>
                      Questions will follow each subject&apos;s preference settings
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {subjects.map((subject) => (
                        <button
                          key={subject.id}
                          onClick={() => toggleSubject(subject.id)}
                          className={`p-3 rounded-xl border-2 text-left transition-colors ${
                            selectedSubjects.includes(subject.id)
                              ? 'border-primary bg-primary/5'
                              : 'border-border hover:border-muted-foreground'
                          }`}
                          data-testid={`select-subject-${subject.id}`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: subject.color }}
                            />
                            <span className="font-medium text-sm truncate">{subject.name}</span>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {subject.lecture_count} lecture{subject.lecture_count !== 1 ? 's' : ''}
                          </p>
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Lecture Selection */}
              <Card className="border-border/50 shadow-card">
                <CardHeader>
                  <CardTitle className="font-serif text-lg flex items-center gap-2">
                    <BookOpen className="w-5 h-5" />
                    Select Specific Lectures
                  </CardTitle>
                  <CardDescription>
                    Optionally add individual lectures
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {lectures.length === 0 ? (
                    <p className="text-muted-foreground text-center py-4">
                      No lectures with content available
                    </p>
                  ) : (
                    <ScrollArea className="h-48">
                      <div className="space-y-2 pr-4">
                        {lectures.map((lecture) => {
                          const subject = subjects.find(s => s.id === lecture.subject_id);
                          return (
                            <label
                              key={lecture.id}
                              className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted cursor-pointer"
                            >
                              <Checkbox
                                checked={selectedLectures.includes(lecture.id)}
                                onCheckedChange={() => toggleLecture(lecture.id)}
                              />
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm truncate">{lecture.title}</p>
                                {subject && (
                                  <div className="flex items-center gap-1">
                                    <div
                                      className="w-2 h-2 rounded-full"
                                      style={{ backgroundColor: subject.color }}
                                    />
                                    <span className="text-xs text-muted-foreground">{subject.name}</span>
                                  </div>
                                )}
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>

              {/* Question Count */}
              <Card className="border-border/50 shadow-card">
                <CardHeader>
                  <CardTitle className="font-serif text-lg flex items-center gap-2">
                    <Target className="w-5 h-5" />
                    Number of Questions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4">
                    <Input
                      type="number"
                      min={1}
                      max={100}
                      value={totalQuestions}
                      onChange={(e) => setTotalQuestions(Math.max(1, Math.min(100, parseInt(e.target.value) || 10)))}
                      className="w-24"
                      data-testid="question-count-input"
                    />
                    <span className="text-muted-foreground">questions (1-100)</span>
                  </div>
                </CardContent>
              </Card>

              {/* Start Button */}
              <div className="flex justify-center pt-4">
                <Button
                  size="lg"
                  onClick={startReview}
                  disabled={loadingQuestion || (selectedSubjects.length === 0 && selectedLectures.length === 0)}
                  className="rounded-xl px-8"
                  data-testid="start-review-btn"
                >
                  {loadingQuestion ? (
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  ) : (
                    <Play className="w-5 h-5 mr-2" />
                  )}
                  Start Review
                </Button>
              </div>
            </div>
          )}
        </main>
      </div>
    );
  }

  // ==================== Review Session Screen ====================
  return (
    <div className="min-h-screen bg-background noise-bg">
      <header className="glass sticky top-0 z-50">
        <div className="mx-auto flex h-auto min-h-16 max-w-4xl flex-wrap items-center justify-between gap-3 px-4 py-3 sm:h-16 sm:min-h-0 sm:flex-nowrap sm:px-6 sm:py-0">
          <div className="flex items-center gap-4">
            <Link to="/review">
              <Button variant="ghost" size="icon">
                <ChevronLeft className="w-5 h-5" />
              </Button>
            </Link>
            <span className="font-serif text-xl text-foreground">Quick Review</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle2 className="w-4 h-4 text-green-500" />
              <span>{session?.correct_count || 0}</span>
              <XCircle className="w-4 h-4 text-red-500 ml-2" />
              <span>{session?.incorrect_count || 0}</span>
            </div>
            {!isSessionComplete && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={createSessionReviewNote}>
                    <FileText className="w-4 h-4 mr-2" />
                    Make Review Note
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => {
                    setSessionToComplete(session);
                    setCompleteDialogOpen(true);
                  }}>
                    <Check className="w-4 h-4 mr-2" />
                    End Session Early
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-6 sm:px-6 sm:py-8">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between text-sm text-muted-foreground mb-2">
            <span>Progress</span>
            <span>{session?.answers?.length || 0} / {session?.total_questions} answered</span>
          </div>
          <Progress value={getProgressPercentage()} className="h-2" />
        </div>

        {/* Session Complete */}
        {isSessionComplete && (
          <Card className="border-border/50 shadow-card mb-8">
            <CardContent className="pt-6 text-center">
              <Award className="w-16 h-16 text-primary mx-auto mb-4" />
              <h2 className="font-serif text-2xl mb-2">Review Complete!</h2>
              <p className="text-muted-foreground mb-4">
                You got {session?.correct_count || 0} out of {session?.total_questions} correct
                ({Math.round(((session?.correct_count || 0) / session?.total_questions) * 100)}%)
              </p>
              <div className="flex justify-center gap-4">
                <Button variant="outline" onClick={createSessionReviewNote}>
                  <FileText className="w-4 h-4 mr-2" />
                  Make Review Note
                </Button>
                <Button variant="outline" onClick={() => navigate('/review')}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  New Review
                </Button>
                <Link to="/dashboard">
                  <Button>Back to Dashboard</Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Current Question */}
        {!isSessionComplete && currentQuestion && (
          <Card className="border-border/50 shadow-card mb-6">
            {/* Question Header */}
            <div 
              className="h-2 rounded-t-lg"
              style={{ backgroundColor: currentQuestion.subject_color || '#3b82f6' }}
            />
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium px-2 py-1 rounded-full bg-muted">
                    Q{currentQuestionIndex + 1}
                  </span>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    currentQuestion.difficulty === 'easy' ? 'bg-green-500/10 text-green-600' :
                    currentQuestion.difficulty === 'medium' ? 'bg-amber-500/10 text-amber-600' :
                    'bg-red-500/10 text-red-600'
                  }`}>
                    {currentQuestion.difficulty}
                  </span>
                  <span className="text-xs px-2 py-1 rounded-full bg-blue-500/10 text-blue-600">
                    {currentQuestion.type?.replace('_', ' ')}
                  </span>
                </div>
                {currentQuestion.subject_name && (
                  <div className="flex items-center gap-1">
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: currentQuestion.subject_color }}
                    />
                    <span className="text-xs text-muted-foreground">{currentQuestion.subject_name}</span>
                  </div>
                )}
              </div>
              <CardTitle className="font-serif text-xl mt-4">
                {currentQuestion.question}
              </CardTitle>
              {currentQuestion.topic && (
                <p className="text-sm text-muted-foreground">Topic: {currentQuestion.topic}</p>
              )}
            </CardHeader>
            <CardContent>
              {/* Multiple Choice Options */}
              {currentQuestion.type === 'multiple_choice' && currentQuestion.options && (
                <RadioGroup
                  value={selectedOption}
                  onValueChange={setSelectedOption}
                  disabled={!!evaluation}
                  className="space-y-3"
                >
                  {currentQuestion.options.map((option, idx) => (
                    <label
                      key={idx}
                      className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${
                        evaluation
                          ? option.startsWith(currentQuestion.correct_answer?.charAt(0))
                            ? 'border-green-500 bg-green-500/5'
                            : selectedOption === option.charAt(0)
                            ? 'border-red-500 bg-red-500/5'
                            : 'border-border'
                          : selectedOption === option.charAt(0)
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-muted-foreground'
                      }`}
                    >
                      <RadioGroupItem value={option.charAt(0)} />
                      <span>{option}</span>
                    </label>
                  ))}
                </RadioGroup>
              )}

              {/* Text Answer */}
              {currentQuestion.type !== 'multiple_choice' && (
                <Textarea
                  value={userAnswer}
                  onChange={(e) => setUserAnswer(e.target.value)}
                  placeholder={
                    currentQuestion.type === 'short_form'
                      ? 'Write a brief answer (1-3 sentences)...'
                      : 'Write a detailed answer (2-4 paragraphs)...'
                  }
                  disabled={!!evaluation}
                  rows={currentQuestion.type === 'long_form' ? 8 : 4}
                  className="resize-none"
                  data-testid="answer-textarea"
                />
              )}

              {/* Submit Button */}
              {!evaluation && (
                <Button
                  onClick={submitAnswer}
                  disabled={submittingAnswer || (!selectedOption && !userAnswer.trim())}
                  className="w-full mt-4 rounded-xl"
                  data-testid="submit-answer-btn"
                >
                  {submittingAnswer ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : null}
                  Submit Answer
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Evaluation Feedback */}
        {evaluation && (
          <Card className={`border-2 mb-6 ${
            evaluation.is_correct || evaluation.score >= 70
              ? 'border-green-500 bg-green-500/5'
              : 'border-red-500 bg-red-500/5'
          }`}>
            <CardHeader>
              <div className="flex items-center gap-2">
                {evaluation.is_correct || evaluation.score >= 70 ? (
                  <CheckCircle2 className="w-6 h-6 text-green-500" />
                ) : (
                  <XCircle className="w-6 h-6 text-red-500" />
                )}
                <CardTitle className="font-serif">
                  {evaluation.is_correct || evaluation.score >= 70 ? 'Correct!' : 'Incorrect'}
                </CardTitle>
                <span className="ml-auto text-lg font-bold">
                  {evaluation.score}%
                </span>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="font-medium text-sm mb-1">Correct Answer:</p>
                <p className="text-muted-foreground">{currentQuestion?.correct_answer}</p>
              </div>
              
              <div>
                <p className="font-medium text-sm mb-1">Feedback:</p>
                <p className="text-muted-foreground">{evaluation.feedback}</p>
              </div>

              {evaluation.missing_points?.length > 0 && (
                <div>
                  <p className="font-medium text-sm mb-1">Missing Points:</p>
                  <ul className="list-disc list-inside text-muted-foreground text-sm">
                    {evaluation.missing_points.map((point, idx) => (
                      <li key={idx}>{point}</li>
                    ))}
                  </ul>
                </div>
              )}

              {evaluation.suggestions && (
                <div className="p-3 bg-muted rounded-xl">
                  <p className="font-medium text-sm mb-1 flex items-center gap-1">
                    <Lightbulb className="w-4 h-4" />
                    Suggestion
                  </p>
                  <p className="text-sm text-muted-foreground">{evaluation.suggestions}</p>
                </div>
              )}

              {/* Strengthen this topic - only show for incorrect answers */}
              {(!evaluation.is_correct && evaluation.score < 70) && currentQuestion?.topic && (
                <div className="pt-4 border-t border-border">
                  <div className="flex items-center justify-between p-4 bg-primary/5 rounded-xl border border-primary/20">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                        <Sparkles className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">Strengthen this topic</p>
                        <p className="text-xs text-muted-foreground">
                          Use Learn-assist™ to master "{currentQuestion.topic}"
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="default"
                      size="sm"
                      className="rounded-xl"
                      onClick={() => {
                        // Navigate to Q&A page with Learn-assist tab and topic pre-filled
                        const params = new URLSearchParams({
                          tab: 'learn-assist',
                          topic: currentQuestion.topic,
                          tool: evaluation.score < 30 ? 'first-principles' : 'example-accelerator'
                        });
                        navigate(`/qa?${params.toString()}`);
                      }}
                    >
                      <ArrowRight className="w-4 h-4 mr-1" />
                      Go
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Next Question Controls */}
        {evaluation && !isSessionComplete && (
          <Card className="border-border/50 shadow-card">
            <CardHeader>
              <CardTitle className="font-serif text-lg">Next Question</CardTitle>
              <CardDescription>
                Customize the next question or let the AI choose based on subject preferences
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label className="text-sm">Question Type</Label>
                  <Select value={nextQuestionType} onValueChange={setNextQuestionType}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Auto (from preferences)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto (from preferences)</SelectItem>
                      <SelectItem value="multiple_choice">Multiple Choice</SelectItem>
                      <SelectItem value="short_form">Short Form</SelectItem>
                      <SelectItem value="long_form">Long Form</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm">Difficulty</Label>
                  <Select value={nextDifficulty} onValueChange={setNextDifficulty}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Auto (from preferences)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto (from preferences)</SelectItem>
                      <SelectItem value="easy">Easy</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="advanced">Advanced</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <Button
                onClick={() => generateNextQuestion()}
                disabled={loadingQuestion}
                className="w-full rounded-xl"
                data-testid="next-question-btn"
              >
                {loadingQuestion ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <ChevronRight className="w-4 h-4 mr-2" />
                )}
                Generate Next Question
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Loading Question */}
        {loadingQuestion && !currentQuestion && (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-12 h-12 animate-spin text-primary mb-4" />
            <p className="text-muted-foreground">Generating question...</p>
          </div>
        )}
      </main>

      {/* Mark Complete Dialog (for session view) */}
      <AlertDialog open={completeDialogOpen} onOpenChange={setCompleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>End Session Early?</AlertDialogTitle>
            <AlertDialogDescription>
              This will end the review session. You can still view your results, but you won't be able to answer more questions.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => sessionToComplete && handleMarkComplete(sessionToComplete)}>
              End Session
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
